const HUBSPOT_REFERRAL_UUID =
  '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}'

/** HubSpot cosell account referral create path (optional referral UUID segment). */
export const isHubspotCosellAccountReferralPath = (pathname: string): boolean =>
  /\/portal\/[^/]+(\/cosell)?\/[^/]+\/[^/]+\/referral\/(?:[0-9a-fA-F-]{36}\/)?opportunity/.test(
    pathname
  )

/** View existing referral: .../portal/:portalId/:cloud/:account/referral/:uuid/opportunity */
export const isHubspotViewReferralPath = (pathname: string): boolean =>
  new RegExp(
    `\\/portal\\/[^/]+\\/[^/]+\\/[^/]+\\/referral\\/${HUBSPOT_REFERRAL_UUID}\\/opportunity`
  ).test(pathname)

/** Generic create with template segment: .../referral/opportunity/:templateId */
export const isHubspotGenericTemplateReferralPath = (
  pathname: string
): boolean => /\/portal\/[^/]+\/referral\/opportunity\/[^/]+/.test(pathname)

/**
 * Cosell create, view referral, or template deep-link — never show the create
 * selection modal on these paths (pathname-only; does not depend on flags).
 */
export const isHubspotDirectedReferralPath = (pathname: string): boolean =>
  isHubspotCosellAccountReferralPath(pathname) ||
  isHubspotViewReferralPath(pathname) ||
  isHubspotGenericTemplateReferralPath(pathname)

/**
 * Matches `.../referral/opportunity` or `.../referral/:referralUuid/opportunity`
 * (excludes `/reject` suffix handling via caller checks on `after`).
 */
const REFERRAL_OPPORTUNITY_SEGMENT =
  /\/referral\/(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/)?opportunity/gi

function lastReferralOpportunityMatch(pathname: string): {
  index: number
  length: number
} | null {
  let lastIdx = -1
  let lastLen = 0
  let m: RegExpExecArray | null
  const re = new RegExp(REFERRAL_OPPORTUNITY_SEGMENT.source, 'gi')
  while ((m = re.exec(pathname)) !== null) {
    lastIdx = m.index
    lastLen = m[0].length
  }
  if (lastIdx === -1) return null
  return { index: lastIdx, length: lastLen }
}

/**
 * Inserts or replaces the mapping template id in HubSpot external referral URLs:
 * `.../referral/opportunity` → `.../referral/opportunity/:templateId`
 * `.../referral/:referralId/opportunity` → `.../referral/:referralId/opportunity/:templateId`
 * Leaves `.../referral/.../opportunity/reject` unchanged.
 */
export function buildHubspotReferralPathWithTemplate(
  pathname: string,
  templateId: string
): string {
  if (!templateId) return pathname

  const match = lastReferralOpportunityMatch(pathname)
  if (!match) return pathname

  const before = pathname.slice(0, match.index + match.length)
  const after = pathname.slice(match.index + match.length)

  if (after.startsWith('/reject')) return pathname

  if (after === '' || after === '/' || after.startsWith('/')) {
    return `${before}/${templateId}`
  }
  return pathname
}
